import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { alpha } from "@mui/material/styles";

import { PROMOTION_SCRATCH_ENABLED } from "../../components/PromotionScratchCard";
import { api, errorMessage } from "./api";
import { brand } from "./adminTheme";
import { CreatePromoLinkRequest, ProductCatalogItem, PromoLink, PromoLinkContent, PromoLinkStats, SupportSession } from "./types";

type Operation =
  | { kind: "upload"; file: File }
  | { kind: "create"; request: CreatePromoLinkRequest }
  | { kind: "content"; id: string; content: PromoLinkContent }
  | { kind: "resume"; id: string }
  | { kind: "disable"; id: string; idempotencyKey: string };

const emptyContent: PromoLinkContent = { title: "", message: "", image_url: null, image_alt: "" };

function uuid(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

function imageUrlValid(value: string): boolean {
  if (/^\/(?!\/)/.test(value)) return !/[\\\s]/.test(value);
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}

function shareUrl(link: PromoLink): string {
  return new URL(link.url, window.location.origin).href;
}

function status(link: PromoLink): string {
  if (link.disabled) return "Disabled";
  if (!link.provisioned) return "Setup pending";
  if (link.expires_at && new Date(link.expires_at).getTime() <= Date.now()) return "Expired";
  if (!link.active) return "Unavailable";
  return "Active";
}

function PromoLinkResults({ link, csrf }: { link: PromoLink; csrf: string }) {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState<PromoLinkStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setOpen(true);
    setLoading(true);
    setError("");
    try {
      setStats(await api.promoLinkStats(link.id, csrf));
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  };

  const metrics = stats && [
    ["Checkouts started", stats.checkout_starts.toLocaleString()],
    ["Desktop checkouts", stats.checkout_starts_desktop.toLocaleString()],
    ["Mobile checkouts", stats.checkout_starts_mobile.toLocaleString()],
    ["Code redemptions", stats.times_redeemed === null ? "Unavailable" : stats.times_redeemed.toLocaleString()],
  ];

  return <Box component="section" aria-label={`Results for ${link.code}`} sx={{ mt: 2 }}>
    <Button onClick={load} disabled={loading} aria-expanded={open}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
      {open ? "Refresh results" : "View results"}
    </Button>
    {open && <Stack spacing={1.5} sx={{ mt: 1 }} aria-busy={loading}>
      {error && <Alert severity="error">{error} {stats ? "Showing the previous results. Refresh to retry." : "Refresh results to retry."}</Alert>}
      {metrics && <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
        {metrics.map(([label, value]) => <Box key={label}>
          <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
          <Typography component="dd" variant="h6" sx={{ m: 0 }}>{value}</Typography>
        </Box>)}
      </Box>}
      {stats?.times_redeemed === null && <Alert severity="warning">Code redemptions could not be loaded from Stripe. Refresh results to try again.</Alert>}
      <Typography variant="caption" color="text.secondary">
        Checkouts count sessions created from this sale link since tracking began, using the page layout when checkout starts.
        {" "}Redemptions are lifetime uses of the code reported by Stripe, including uses outside this link.
      </Typography>
    </Stack>}
  </Box>;
}

function PromoPreview({ content, code, percentage, productName }: {
  content: PromoLinkContent; code: string; percentage: string; productName: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [content.image_url]);
  const imageValid = content.image_url && imageUrlValid(content.image_url);
  return (
    <Paper variant="outlined" component="section" aria-label="Popup preview" sx={{ overflow: "hidden", borderColor: alpha(brand.accent, 0.3) }}>
      {imageValid && !imageFailed ? <Box component="img" src={content.image_url!} alt={content.image_alt || "Promo image preview"}
        onError={() => setImageFailed(true)} sx={{ display: "block", width: "100%", maxHeight: 250, objectFit: "cover" }} />
        : <Box sx={{ minHeight: 96, p: 3, display: "grid", placeItems: "center", bgcolor: alpha(brand.accent, 0.06) }}>
          <Typography variant="caption" color="text.secondary">{imageFailed ? "Image could not be loaded" : "Optional promo image"}</Typography>
        </Box>}
      <Stack spacing={2} sx={{ p: 3 }}>
        <Typography variant="overline" color="secondary.main">{productName || "Product offer"} · {percentage || "0"}% off</Typography>
        <Typography component="h3" variant="h5" sx={{ overflowWrap: "anywhere" }}>{content.title || "Your popup headline"}</Typography>
        <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} color="text.secondary">{content.message || "Describe your offer and why visitors should try this product."}</Typography>
        <Typography variant="caption">Promo code: <Box component="strong" sx={{ color: "text.primary" }}>{code || "YOURCODE"}</Box></Typography>
        <Button variant="contained" tabIndex={-1} sx={{ pointerEvents: "none" }} aria-disabled="true">Buy now · {percentage || "0"}% off</Button>
        <Typography variant="caption" color="text.secondary">{PROMOTION_SCRATCH_ENABLED
          ? "Visitors scratch the code area or select Reveal code to uncover it. The code is filled in automatically at checkout."
          : "Visitors see the promo code immediately. The code is filled in automatically at checkout."}</Typography>
      </Stack>
    </Paper>
  );
}

export default function PromoLinksPanel({ session, onSession }: {
  session: SupportSession; onSession?: (value: SupportSession) => void;
}) {
  const [csrf, setCsrf] = useState(session.csrf_token);
  const editable = session.administrator.role === "manager";
  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const [links, setLinks] = useState<PromoLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Operation | null>(null);
  const [editing, setEditing] = useState<PromoLink | null>(null);
  const [disableSelection, setDisableSelection] = useState<PromoLink | null>(null);
  const [name, setName] = useState("");
  const [productId, setProductId] = useState("");
  const [code, setCode] = useState("");
  const [percentage, setPercentage] = useState("20");
  const [redemptions, setRedemptions] = useState("100");
  const [duration, setDuration] = useState("168");
  const [neverExpires, setNeverExpires] = useState(false);
  const [content, setContent] = useState<PromoLinkContent>(emptyContent);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [reauthError, setReauthError] = useState("");
  const [reauthBusy, setReauthBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const locked = busy || Boolean(pending) || reauthBusy;

  useEffect(() => setCsrf(session.csrf_token), [session.csrf_token]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [products, records] = await Promise.all([api.promoProducts(), api.promoLinks(csrf)]);
      setCatalog(products);
      setLinks(records);
    } catch (reason) {
      setLoadError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  }, [csrf]);
  useEffect(() => { load(); }, [load]);

  const execute = async (operation: Operation, csrfToken: string) => {
    if (!editable) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (operation.kind === "upload") {
        const uploaded = await api.uploadPromoImage(operation.file, csrfToken);
        setContent(current => ({ ...current, image_url: uploaded.image_url }));
        setPending(null);
        setNotice("Image uploaded. Save the promo to use it.");
        return;
      }
      let result: PromoLink;
      if (operation.kind === "create") result = await api.createPromoLink(operation.request, csrfToken);
      else if (operation.kind === "content") result = await api.updatePromoLinkContent(operation.id, operation.content, csrfToken);
      else if (operation.kind === "resume") result = await api.resumePromoLink(operation.id, csrfToken);
      else result = await api.disablePromoLink(operation.id, operation.idempotencyKey, csrfToken);
      setLinks(current => [result, ...current.filter(item => item.id !== result.id)]);
      setPending(null);
      setNotice(operation.kind === "create" || operation.kind === "resume" ? "Promo link created. Copy it below to share your offer."
        : operation.kind === "content" ? "Popup appearance updated." : "Promo link disabled.");
      if (operation.kind === "content") {
        setEditing(null);
        setContent(emptyContent);
      }
    } catch (reason) {
      const response = axios.isAxiosError(reason) ? reason.response : undefined;
      if (response?.data?.detail?.code === "recent_auth_required") {
        setReauthError("");
        setReauthOpen(true);
      } else {
        if (response && response.status < 500 && response.data?.detail?.code !== "promo_setup_pending") setPending(null);
        setError(errorMessage(reason));
      }
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = (file: File | undefined) => {
    if (!file || locked || !editable) return;
    if (file.size > 8 * 1024 * 1024 || file.size === 0) {
      setError("Choose an image smaller than 8 MB."); return;
    }
    if (file.type && !["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPEG, PNG, or WebP image."); return;
    }
    const operation: Operation = { kind: "upload", file };
    setPending(operation);
    execute(operation, csrf);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (locked || !editable) return;
    const normalizedContent = {
      title: content.title.trim(), message: content.message.trim(),
      image_url: content.image_url?.trim() || null, image_alt: content.image_alt.trim(),
    };
    if (!normalizedContent.title || !normalizedContent.message) {
      setError("Add a popup headline and message."); return;
    }
    if (normalizedContent.image_url && (!imageUrlValid(normalizedContent.image_url) || !normalizedContent.image_alt)) {
      setError("Use an HTTPS or site-relative image URL and add an image description."); return;
    }
    let operation: Operation;
    if (editing) operation = { kind: "content", id: editing.id, content: normalizedContent };
    else {
      if (!name.trim() || !productId || !/^[A-Za-z0-9]{1,32}$/.test(code.trim())) {
        setError("Choose a product, name your campaign, and use 1–32 letters or numbers for the promo code."); return;
      }
      if (!Number.isInteger(Number(percentage)) || Number(percentage) < 1 || Number(percentage) > 100
        || !Number.isInteger(Number(redemptions)) || Number(redemptions) < 1 || Number(redemptions) > 1000
        || (!neverExpires && (!Number.isInteger(Number(duration)) || Number(duration) < 1 || Number(duration) > 720))) {
        setError("Choose a discount from 1–100%, 1–1,000 redemptions, and a validity of 1–720 hours or Never expires."); return;
      }
      operation = { kind: "create", request: {
        ...normalizedContent, name: name.trim(), product_id: productId, custom_code: code.trim().toUpperCase(),
        percentage: Number(percentage), max_redemptions: Number(redemptions),
        expires_at: neverExpires ? null : new Date(Date.now() + Number(duration) * 3600000).toISOString(), idempotency_key: uuid(),
      } };
    }
    setPending(operation);
    execute(operation, csrf);
  };

  const edit = (link: PromoLink) => {
    if (locked) return;
    setEditing(link);
    setContent({ title: link.title, message: link.message, image_url: link.image_url, image_alt: link.image_alt });
    setError("");
    setNotice("");
  };

  const reauthenticate = async (event: FormEvent) => {
    event.preventDefault();
    if (!pending || reauthBusy) return;
    setReauthBusy(true);
    setReauthError("");
    try {
      const updated = await api.reauthenticate(password, csrf);
      setCsrf(updated.csrf_token);
      onSession?.(updated);
      setReauthOpen(false);
      setPassword("");
      await execute(pending, updated.csrf_token);
    } catch (reason) {
      setReauthError(errorMessage(reason));
    } finally {
      setReauthBusy(false);
    }
  };

  const copy = async (link: PromoLink) => {
    try {
      await navigator.clipboard.writeText(shareUrl(link));
      setNotice(`Promo link copied for ${link.code}.`);
    } catch {
      setError("Could not copy the link. Select and copy it from its link field.");
    }
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography component="h2" variant="h5">Product promo links</Typography>
        <Typography color="text.secondary">Create a product offer with a custom code, a shareable link, and a popup designed by you.</Typography>
      </Stack>
      {loadError && <Alert severity="error" action={<Button color="inherit" disabled={loading} onClick={load}>Reload</Button>}>{loadError}</Alert>}
      {error && <Alert severity="error" aria-live="assertive">{error}</Alert>}
      {notice && <Alert severity="success" role="status">{notice}</Alert>}
      {pending && !busy && !reauthOpen && <Alert severity="warning" action={<Button color="inherit" onClick={() => execute(pending, csrf)}>Retry same request</Button>}>
        This request has not been confirmed. Retry to recover its result before making further changes.
      </Alert>}
      {editable ? <Paper component="form" onSubmit={submit} sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography component="h3" variant="h6" sx={{ mb: 2.5 }}>{editing ? `Edit popup for ${editing.code}` : "Create a promo link"}</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.4fr) minmax(280px, 1fr)" }, gap: 3, alignItems: "start" }}>
          <Stack spacing={2}>
            {editing ? <Alert severity="info">The product, discount, code, and expiry stay as created. Save to update the popup shown by this link.</Alert>
              : <>
                <TextField id="promo-campaign-name" label="Campaign name" value={name} required disabled={locked}
                  inputProps={{ maxLength: 120 }} helperText="Internal name for this offer." onChange={event => setName(event.target.value)} />
                <TextField id="promo-product" label="Product" select value={productId} required disabled={locked || loading || !catalog.length}
                  onChange={event => setProductId(event.target.value)}>
                  {catalog.map(product => <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>)}
                </TextField>
                <TextField id="promo-custom-code" label="Custom promo code" value={code} required disabled={locked}
                  helperText="1–32 letters or numbers. Codes are saved in uppercase." inputProps={{ maxLength: 32, pattern: "[A-Za-z0-9]+" }}
                  onChange={event => setCode(event.target.value)} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <TextField id="promo-discount" label="Discount (%)" type="number" value={percentage} required disabled={locked}
                    inputProps={{ min: 1, max: 100, step: 1 }} onChange={event => setPercentage(event.target.value)} />
                  <TextField id="promo-redemptions" label="Maximum redemptions" type="number" value={redemptions} required disabled={locked}
                    inputProps={{ min: 1, max: 1000, step: 1 }} onChange={event => setRedemptions(event.target.value)} />
                </Box>
                <FormControlLabel label="Never expires" control={<Checkbox checked={neverExpires} disabled={locked}
                  onChange={event => setNeverExpires(event.target.checked)} />} />
                <TextField id="promo-validity" label="Validity (hours)" type="number" value={duration} required={!neverExpires} disabled={locked || neverExpires}
                  helperText={neverExpires ? "No expiration date. The redemption limit still applies." : "Expires after this many hours, up to 30 days."}
                  inputProps={{ min: 1, max: 720, step: 1 }} onChange={event => setDuration(event.target.value)} />
                <Divider />
              </>}
            <TextField id="promo-headline" label="Popup headline" value={content.title} required disabled={locked}
              inputProps={{ maxLength: 160 }} onChange={event => setContent({ ...content, title: event.target.value })} />
            <TextField id="promo-message" label="Popup message" value={content.message} multiline minRows={4} required disabled={locked}
              inputProps={{ maxLength: 4000 }} helperText="Plain text. Line breaks are preserved." onChange={event => setContent({ ...content, message: event.target.value })} />
            <Stack spacing={1}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button variant="outlined" disabled={locked} onClick={() => fileInput.current?.click()}
                  startIcon={busy && pending?.kind === "upload" ? <CircularProgress size={16} color="inherit" /> : <UploadFileOutlinedIcon />}>
                  {busy && pending?.kind === "upload" ? "Uploading image…" : "Upload image from computer"}
                </Button>
                <Button disabled={locked || !content.image_url} onClick={() => setContent(current => ({ ...current, image_url: null }))}>Remove image</Button>
              </Stack>
              <input ref={fileInput} type="file" hidden aria-label="Upload promo image" accept="image/jpeg,image/png,image/webp"
                disabled={locked} onChange={event => { const file = event.target.files?.[0]; event.target.value = ""; uploadImage(file); }} />
              <Typography variant="caption" color="text.secondary">JPEG, PNG, or WebP, up to 8 MB. Uploads are stored on the server and optimized automatically.</Typography>
            </Stack>
            <TextField id="promo-image-url" label="Image URL (optional)" value={content.image_url || ""} disabled={locked}
              helperText="Filled automatically after uploading. You can also enter an existing HTTPS image URL or website path."
              inputProps={{ maxLength: 2000 }} onChange={event => setContent({ ...content, image_url: event.target.value || null })} />
            <TextField id="promo-image-alt" label="Image description" value={content.image_alt} disabled={locked} required={Boolean(content.image_url)}
              inputProps={{ maxLength: 200 }} helperText="Describe the image for visitors using screen readers."
              onChange={event => setContent({ ...content, image_alt: event.target.value })} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="contained" type="submit" disabled={locked || (!editing && (loading || !catalog.length))} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LinkOutlinedIcon />}>
                {editing ? "Save popup appearance" : "Create promo link"}
              </Button>
              {editing && <Button disabled={locked} onClick={() => { setEditing(null); setContent(emptyContent); }}>Cancel editing</Button>}
            </Stack>
          </Stack>
          <Stack spacing={1} sx={{ position: { lg: "sticky" }, top: 24 }}>
            <Typography variant="overline" color="text.secondary">Live popup preview</Typography>
            <PromoPreview content={content} code={editing?.code || code.toUpperCase()} percentage={editing ? String(editing.percentage) : percentage}
              productName={editing?.product_name || catalog.find(product => product.id === productId)?.name || ""} />
          </Stack>
        </Box>
      </Paper> : <Alert severity="info">A support manager can create promo links and edit or disable existing offers.</Alert>}
      <Box component="section" aria-label="Existing promo links">
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography component="h3" variant="h6">Your promo links</Typography>
          <Button disabled={loading || locked} onClick={load}>Refresh links</Button>
        </Stack>
        {loading && <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={24} aria-label="Loading promo links" /></Box>}
        {!loading && !loadError && links.length === 0 && <Paper variant="outlined" sx={{ p: 3 }}><Typography color="text.secondary">No promo links yet. Create your first product offer above.</Typography></Paper>}
        <Stack spacing={2}>
          {links.map(link => <Paper key={link.id} variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="h4" variant="h6" sx={{ overflowWrap: "anywhere" }}>{link.name}</Typography>
                <Typography variant="body2" color="text.secondary">{link.product_name} · {link.percentage}% off · {link.code}</Typography>
              </Box>
              <Chip size="small" label={status(link)} color={status(link) === "Active" ? "success" : "default"} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 2 }}>
              Up to {link.max_redemptions.toLocaleString()} redemptions · {link.expires_at ? `Expires ${new Date(link.expires_at).toLocaleString()}` : "Never expires"}
            </Typography>
            {link.provisioned ? <TextField id={`promo-link-${link.id}`} label={`Promo link for ${link.code}`} value={shareUrl(link)} fullWidth
              InputProps={{ readOnly: true }} inputProps={{ onFocus: (event: React.FocusEvent<HTMLInputElement>) => event.target.select() }} />
              : !link.disabled && <Alert severity="warning">This offer is still being set up. Resume setup to finish before sharing it.</Alert>}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
              <Button startIcon={<ContentCopyIcon />} disabled={!link.provisioned} onClick={() => copy(link)}>Copy link</Button>
              {link.provisioned && <Button component="a" href={shareUrl(link)} target="_blank" rel="noopener noreferrer">Open product offer</Button>}
              {editable && <Button disabled={locked} onClick={() => edit(link)}>Edit popup</Button>}
              {editable && !link.provisioned && !link.disabled && <Button disabled={locked} onClick={() => {
                const operation: Operation = { kind: "resume", id: link.id };
                setPending(operation); execute(operation, csrf);
              }}>Resume setup</Button>}
              {editable && (!link.disabled || !link.stripe_disabled) && <Button startIcon={<BlockOutlinedIcon />} color="warning" disabled={locked}
                onClick={() => setDisableSelection(link)}>{link.disabled ? "Retry disable" : "Disable link"}</Button>}
            </Stack>
            <PromoLinkResults link={link} csrf={csrf} />
          </Paper>)}
        </Stack>
      </Box>
      <Dialog open={Boolean(disableSelection)} onClose={() => !busy && setDisableSelection(null)} fullWidth maxWidth="xs" aria-labelledby="promo-disable-title">
        <DialogTitle id="promo-disable-title">Disable this promo link?</DialogTitle>
        <DialogContent><Typography>New visitors will no longer see this offer, and code {disableSelection?.code} will stop accepting new redemptions.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableSelection(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => {
            if (!disableSelection || locked) return;
            const operation: Operation = { kind: "disable", id: disableSelection.id, idempotencyKey: uuid() };
            setDisableSelection(null); setPending(operation); execute(operation, csrf);
          }}>Disable offer</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={reauthOpen} onClose={() => { if (!reauthBusy) { setReauthOpen(false); setPassword(""); } }}
        component="form" onSubmit={reauthenticate} fullWidth maxWidth="xs" aria-labelledby="promo-reauth-title">
        <DialogTitle id="promo-reauth-title">Confirm your password</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">Promo links require recent manager authentication. Your original request will continue after confirmation.</Typography>
            {reauthError && <Alert severity="error">{reauthError}</Alert>}
            <TextField id="promo-reauth-password" autoFocus label="Password" type="password" autoComplete="current-password" required fullWidth
              disabled={reauthBusy} value={password} onChange={event => setPassword(event.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={reauthBusy} onClick={() => { setReauthOpen(false); setPassword(""); }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={reauthBusy}>Confirm and continue</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
